import { useMemo, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { AlertTriangle, ClipboardCheck, Settings } from "lucide-react";
import { DateField, Button, CheckboxField, Field, Modal, TextareaField } from "@fawkes/ui";

import { Werklijst, WeergaveKeuze, useWeergave, type Kolom, type Rij } from "../components/WerklijstWeergave";
import { PensionLijstFilters, PensionLijstKop, PensionLijstMeta, PensionLijstTegel, zoekIn } from "../components/PensionLijstKop";
import { formatDate, todayIso } from "../i18n/format";
import "./PensionWerklijst.css";

type Reden = "NOOIT_GEDAAN" | "INTERVAL" | "DAG_VOOR_VERTREK" | "GEDAAN";
type Bevinding = "OK" | "BIJZONDERHEID";

interface Instellingen {
  intervalDagen: number;
  dagVoorVertrek: boolean;
}

interface Regel {
  dierId: number;
  dierNaam: string | null;
  diersoort: string | null;
  klantId: number | null;
  klantNaam: string | null;
  kennel: string | null;
  vertrek: string | null;
  laatsteCheck: string | null;
  laatsteDoor: string | null;
  laatsteBevinding: Bevinding | null;
  laatsteOpmerking: string | null;
  teDoen: boolean;
  reden: Reden;
}

interface BodycheckData {
  vestigingId: number;
  datum: string;
  instellingen: Instellingen;
  telling: { teDoen: number; gedaan: number; metBijzonderheid: number; nooitGedaan: number };
  regels: Regel[];
}

/** De soortfilters. "Overig" vangt alles wat geen hond of kat is, zodat er nooit iets wegvalt. */
type Soort = "ALLE" | "HOND" | "KAT" | "OVERIG";

function soortVan(diersoort: string | null): Exclude<Soort, "ALLE"> {
  const s = (diersoort ?? "").toLowerCase();
  if (s.startsWith("hond")) return "HOND";
  if (s.startsWith("kat") || s.startsWith("poes")) return "KAT";
  return "OVERIG";
}

/**
 * Het overzicht "Bodycheck" — wens §4 uit de wensenlijst.
 *
 * <p>Dezelfde vorm als Wegen: elke gast van vandaag staat erop met de reden waarom hij aan de beurt
 * is, en je vinkt af in de rij zelf. Het verschil is wat er wordt vastgelegd — wegen levert een
 * getal dat zichzelf beoordeelt, een bodycheck is een oordeel van de verzorger. Vandaar twee
 * knoppen: <b>OK</b> voor "niets aan de hand", en <b>Bijzonderheid</b> als er iets gezien is, met
 * een regel erbij.
 *
 * <p>De initialen staan bovenaan en niet per rij: een verzorger loopt zijn ronde en is de hele ronde
 * dezelfde persoon. Eén keer intikken en daarna doorklikken is het verschil tussen een lijst die
 * wordt bijgehouden en een lijst die dat niet wordt.
 */
export function PensionBodycheck({ onOpenDier }: { onOpenDier?: (dierId: number) => void }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";
  const [datum, setDatum] = useState(() => todayIso());
  const [soort, setSoort] = useState<Soort>("ALLE");
  const [zoek, setZoek] = useState("");
  const [weergave, setWeergave] = useWeergave("bodycheck");
  const [initialen, setInitialen] = useState("");
  const [instellingenOpen, setInstellingenOpen] = useState(false);
  const [bijzonderheidVoor, setBijzonderheidVoor] = useState<Regel | null>(null);
  const [fout, setFout] = useState<number | null>(null);

  const query = useMemo(() => ({ datum }), [datum]);
  const { data, isLoading, isError, refetch } = useCustom<BodycheckData>({
    url: "pension/bodycheck",
    method: "get",
    config: { query },
  });
  const { mutate: registreer, isLoading: bezig } = useCustomMutation();

  const bodycheck = data?.data;
  const alle = bodycheck?.regels ?? [];
  const regels = zoekIn(soort === "ALLE" ? alle : alle.filter((r) => soortVan(r.diersoort) === soort), zoek);

  const aantalPerSoort = (s: Soort) =>
    s === "ALLE" ? alle.length : alle.filter((r) => soortVan(r.diersoort) === s).length;

  function afvinken(regel: Regel, bevinding: Bevinding, opmerking?: string) {
    setFout(null);
    registreer(
      {
        url: "pension/bodycheck",
        method: "post",
        values: { dierId: regel.dierId, initialen: initialen.trim() || null, bevinding, opmerking },
      },
      {
        onSuccess: () => {
          setBijzonderheidVoor(null);
          refetch();
        },
        onError: () => setFout(regel.dierId),
      },
    );
  }

  // Tabel en tegels komen uit dezelfde kolommen (WerklijstWeergave).
  const kolommen: Kolom<(typeof regels)[number]>[] = [
    {
      kop: t("pensionBodycheck.kolom.dier"),
      cel: (r) => (
        <>
          {onOpenDier ? (
            <button
              type="button"
              className="pension-werklijst__link"
              onClick={() => onOpenDier(r.dierId)}
            >
              {r.dierNaam ?? "—"}
            </button>
          ) : (
            (r.dierNaam ?? "—")
          )}
          {r.diersoort && <span className="pension-chip">{r.diersoort}</span>}
        </>
      ),
      rol: "dier",
    },
    {
      kop: t("pensionBodycheck.kolom.klant"),
      cel: (r) => <>{r.klantNaam ?? "—"}</>,
      rol: "klant",
    },
    {
      kop: t("pensionBodycheck.kolom.kennel"),
      cel: (r) => <>{r.kennel ?? "—"}</>,
      rol: "verblijf",
    },
    {
      kop: t("pensionBodycheck.kolom.laatst"),
      cel: (r) => (
        <>
          {r.laatsteCheck ? formatDate(r.laatsteCheck, locale) : "—"}
          {r.laatsteDoor && <span className="pension-werklijst__door"> · {r.laatsteDoor}</span>}
        </>
      ),
    },
    {
      kop: t("pensionBodycheck.kolom.bevinding"),
      cel: (r) => (
        <>
          {r.laatsteBevinding === "BIJZONDERHEID" ? (
            <span className="pension-werklijst__verschil" data-signaal="ACTIE_NODIG">
              <AlertTriangle size={13} aria-hidden="true" />
              {r.laatsteOpmerking || t("pensionBodycheck.bevinding.BIJZONDERHEID")}
            </span>
          ) : r.laatsteBevinding === "OK" ? (
            t("pensionBodycheck.bevinding.OK")
          ) : (
            "—"
          )}
        </>
      ),
    },
    {
      kop: t("pensionBodycheck.kolom.reden"),
      cel: (r) => (
        <>
          <span className="pension-werklijst__reden" data-reden={r.reden}>
            {t(`pensionBodycheck.reden.${r.reden}`)}
          </span>
        </>
      ),
    },
    {
      kop: t("pensionBodycheck.kolom.afvinken"),
      cel: (r) => (
        <>
          <span className="pension-werklijst__invoer">
            <Button
              variant="primary"
              disabled={bezig}
              onClick={() => afvinken(r, "OK")}
            >
              {t("pensionBodycheck.ok")}
            </Button>
            <Button variant="secondary" onClick={() => setBijzonderheidVoor(r)}>
              {t("pensionBodycheck.bijzonderheid")}
            </Button>
            {fout === r.dierId && (
              <span className="fawkes-error" role="status">
                {t("pensionBodycheck.mislukt")}
              </span>
            )}
          </span>
        </>
      ),
      rol: "actie",
    },
  ];
  const rij = (r: (typeof regels)[number]): Rij => ({ key: r.dierId, "data-signaal": r.laatsteBevinding === "BIJZONDERHEID" ? "ACTIE_NODIG" : "OK", "data-tewegen": r.teDoen ? "" : undefined });

  return (
    <div className="pension-werklijst fawkes-pgo">
      <PensionLijstKop icoon={<ClipboardCheck size={22} />} titel={t("pensionBodycheck.title")} subtitel={t("pensionBodycheck.subtitle")} soort={{ opties: (["HOND", "KAT", "OVERIG"] as Soort[]).map((s) => ({ sleutel: s, label: t(`pensionBodycheck.soort.${s}`), aantal: aantalPerSoort(s) })), actief: soort, onKies: (s) => setSoort(s as Soort), totaal: aantalPerSoort("ALLE") }}>
          <PensionLijstTegel waarde={bodycheck?.telling.teDoen} label={t("pensionBodycheck.teller.teDoen")} />
          <PensionLijstTegel waarde={bodycheck?.telling.gedaan} label={t("pensionBodycheck.teller.gedaan")} />
          <PensionLijstTegel
            waarde={bodycheck?.telling.metBijzonderheid}
            label={t("pensionBodycheck.teller.bijzonderheid")}
            toon={bodycheck && bodycheck.telling.metBijzonderheid > 0 ? "rood" : undefined}
          />
          <PensionLijstTegel
            waarde={bodycheck?.telling.nooitGedaan}
            label={t("pensionBodycheck.teller.nooitGedaan")}
          />
      </PensionLijstKop>

      <PensionLijstFilters
        zoek={zoek}
        onZoek={setZoek}
        weergave={<WeergaveKeuze waarde={weergave} onKies={setWeergave} />}
        rechts={
          <div className="pension-werklijst__balk">
            <input
              type="text"
              className="pension-werklijst__initialen"
              value={initialen}
              maxLength={10}
              placeholder={t("pensionBodycheck.initialen")}
              aria-label={t("pensionBodycheck.initialen")}
              onChange={(e) => setInitialen(e.target.value)}
            />
            <label className="po-veld werklijst-datum">
              <span>{t("pensionOverzichten.datum")}</span>
              <DateField className="pension-werklijst__datum"
                value={datum}
                onChange={(e) => e.target.value && setDatum(e.target.value)}
                aria-label={t("pensionBodycheck.datum")}
              />
            </label>
            <Button variant="secondary" onClick={() => setDatum(todayIso())}>
              {t("pensionBodycheck.vandaag")}
            </Button>
            <Button
              variant="secondary"
              onClick={() => setInstellingenOpen(true)}
              aria-label={t("pensionBodycheck.instellingen.knop")}
              title={t("pensionBodycheck.instellingen.knop")}
            >
              <Settings size={18} />
            </Button>
          </div>
        }
      />

      {isError ? (
        <p className="fawkes-muted">{t("pensionBodycheck.loadFailed")}</p>
      ) : (
        <>
          <PensionLijstMeta aantal={regels.length} />
          {isLoading ? (
            <p className="pension-werklijst__leeg">{t("common.loading")}</p>
          ) : regels.length === 0 ? (
            <p className="pension-werklijst__leeg">
              <ClipboardCheck size={26} aria-hidden="true" />
              {t("pensionBodycheck.leeg")}
            </p>
          ) : (
            <Werklijst lijst="bodycheck" regels={regels} kolommen={kolommen} weergave={weergave} rij={rij} dierId={(r) => r.dierId} />
          )}
        </>
      )}

      {bijzonderheidVoor && (
        <BijzonderheidDialoog
          regel={bijzonderheidVoor}
          bezig={bezig}
          onClose={() => setBijzonderheidVoor(null)}
          onOpslaan={(opmerking) => afvinken(bijzonderheidVoor, "BIJZONDERHEID", opmerking)}
        />
      )}

      {instellingenOpen && bodycheck && (
        <InstellingenDialoog
          waarden={bodycheck.instellingen}
          onClose={() => setInstellingenOpen(false)}
          onOpgeslagen={() => {
            setInstellingenOpen(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}


/** Wat er gezien is. Zonder tekst heeft "bijzonderheid" geen betekenis, dus opslaan kan niet leeg. */
function BijzonderheidDialoog({
  regel,
  bezig,
  onClose,
  onOpslaan,
}: {
  regel: Regel;
  bezig: boolean;
  onClose: () => void;
  onOpslaan: (opmerking: string) => void;
}) {
  const t = useTranslate();
  const [opmerking, setOpmerking] = useState("");

  return (
    <Modal
      open
      title={t("pensionBodycheck.bijzonderheid.titel", { args: [regel.dierNaam ?? ""] })}
      onClose={onClose}
    >
      <TextareaField
        label={t("pensionBodycheck.bijzonderheid.wat")}
        value={opmerking}
        rows={4}
        onChange={(e) => setOpmerking(e.target.value)}
      />
      <div className="pension-werklijst__dialoogknoppen">
        <Button variant="secondary" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button
          variant="primary"
          disabled={bezig || opmerking.trim().length === 0}
          onClick={() => onOpslaan(opmerking.trim())}
        >
          {t("common.save")}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * De instellingen: om de hoeveel dagen een dier nagelopen hoort te worden, en of dat de dag vóór
 * vertrek ook nog een keer moet.
 */
function InstellingenDialoog({
  waarden,
  onClose,
  onOpgeslagen,
}: {
  waarden: Instellingen;
  onClose: () => void;
  onOpgeslagen: () => void;
}) {
  const t = useTranslate();
  const { mutate, isLoading } = useCustomMutation();
  const [form, setForm] = useState(waarden);
  const [fout, setFout] = useState(false);

  function opslaan() {
    setFout(false);
    mutate(
      { url: "pension/bodycheck/instellingen", method: "put", values: form },
      { onSuccess: () => onOpgeslagen(), onError: () => setFout(true) },
    );
  }

  return (
    <Modal open title={t("pensionBodycheck.instellingen.titel")} onClose={onClose}>
      <p className="fawkes-muted">{t("pensionBodycheck.instellingen.uitleg")}</p>
      {fout && (
        <p className="fawkes-error" role="status">
          {t("pensionBodycheck.instellingen.mislukt")}
        </p>
      )}
      <Field
        label={t("pensionBodycheck.instellingen.interval")}
        value={String(form.intervalDagen)}
        onChange={(e) =>
          setForm((v) => ({ ...v, intervalDagen: Number(e.target.value.replace(",", ".")) || 0 }))
        }
      />
      <CheckboxField
        label={t("pensionBodycheck.instellingen.dagVoorVertrek")}
        checked={form.dagVoorVertrek}
        onChange={(e) => setForm((v) => ({ ...v, dagVoorVertrek: e.target.checked }))}
      />
      <div className="pension-werklijst__dialoogknoppen">
        <Button variant="secondary" onClick={onClose}>
          {t("common.cancel")}
        </Button>
        <Button variant="primary" disabled={isLoading} onClick={opslaan}>
          {t("common.save")}
        </Button>
      </div>
    </Modal>
  );
}
